import { Component, EventEmitter, Input, Output, input } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { NgClass } from "@angular/common";
import { Button } from "./index";
import { SharedModule } from "../modules/shared/shared.module";
import { User, UserCombiPackage, CombiOrder } from "../model";
import { UserCombiOrderSettings } from "../model/usercombiordersettings";
import { CreateUserCombiOrderResult } from "../model/createusercombiorderresult";
import { SelectionListItem } from "../model/selectionlistitem";
import { CombiOrderService } from "../services/combiorder/combiorder.service";
import { UserService } from "../services/user/user.service";
import { logAddPaymentInfo, logPurchase } from "../util/analytics.util";

@Component({
  selector: 'app-order-form',
  standalone: true,
  styleUrls: ['./OrderForm.css'],
  imports: [Button, NgClass, SharedModule, TranslateModule],
  template: `
    <form>
      <div class="order-form-item">
        <label class="order-form-label" for="companyName" translate="MODAL_COMBIORDER_COMPANYNAME"></label>
        <input id="companyName" name="companyName" type="text" required
               [(ngModel)]="settings.companyName" class="order-form-input"/>
      </div>

      <div class="order-form-item">
        <label class="order-form-label" for="userName" translate="MODAL_COMBIORDER_NAME"></label>
        <input id="userName" name="userName" type="text" required
               [(ngModel)]="settings.name" class="order-form-input"/>
      </div>

      <div class="order-form-item">
        <label class="order-form-label" for="street" translate="MODAL_COMBIORDER_STREET"></label>
        <input id="street" name="street" type="text" required
               [(ngModel)]="settings.street" class="order-form-input"/>
      </div>

      <div class="order-form-item">
        <label class="order-form-label" for="zipcode" translate="MODAL_COMBIORDER_ZIPCODE"></label>
        <input id="zipcode" name="zipcode" type="text" required
               [(ngModel)]="settings.zipcode" class="order-form-input"/>
      </div>

      <div class="order-form-item">
        <label class="order-form-label" for="city" translate="MODAL_COMBIORDER_CITY"></label>
        <input id="city" name="city" type="text" required
               [(ngModel)]="settings.city" class="order-form-input"/>
      </div>

      <div class="order-form-item">
        <label class="order-form-label" for="telephoneNumber" translate="MODAL_COMBIORDER_PHONE"></label>
        <input id="telephoneNumber" name="telephoneNumber" type="text"
               [(ngModel)]="settings.telephone" class="order-form-input"/>
      </div>

      <app-email [email]="settings.invoiceEmail"
                 (emailChange)="settings.invoiceEmail = $event"
                 label="MODAL_COMBIORDER_INVOICEEMAIL"
                 [order]="true"
                 [ngClass]="'order-form-item'">
      </app-email>

      <div class="order-form-item">
        <label class="order-form-label" for="vatNumber" translate="MODAL_COMBIORDER_VATNUMBER"></label>
        <input id="vatNumber" name="vatNumber" type="text"
               [(ngModel)]="settings.vatNumber" class="order-form-input"/>
        <p class="-mt-2 col-span-2 col-start-2 text-xxs text-gray-400"
           [innerHTML]="'MODAL_COMBIORDER_VATNUMBER_DESCRIPTION' | translate"></p>
      </div>

      <div class="order-form-item">
        <label class="order-form-label mt-2" translate="MODAL_COMBIORDER_PAYMENT_FREQUENCY"></label>
        <div class="col-span-2">
          <app-dropdown
              [hasSelectedItem]="true"
              [items]="getPaymentFrequencies()"
              [selectedItemDisplayName]="('MODAL_COMBIORDER_PERIOD_' + settings.period) | translate"
              (itemSelected)="settings.period = $event.value"></app-dropdown>
          @if (settings.period === 'YEAR') {
            <span class="pl-3 text-xxs text-gray-400"
                  [innerHTML]="'MODAL_COMBIORDER_COST_DESCRIPTION' | translate"></span>
          }
        </div>
      </div>

      <div class="pt-12 text-right text-xs">
        <span translate="MODAL_COMBIORDER_PAY" class="mr-1"></span>
        <span class="mr-1" [innerHTML]="(settings.period === 'MONTH' ? 'MODAL_COMBIORDER_BUTTON_MONTH' : 'MODAL_COMBIORDER_BUTTON_YEAR') | translate: {amount: settings.period === 'MONTH' ? combiOrder()?.monthlyAmount : combiOrder()?.yearlyDiscountedAmount}"></span>
        <span translate="MODAL_COMBIORDER_PAY_EXCL_VAT" class="mr-2"></span>
        <Button type="submit" color="blue" size="xsmall" [shadow]="false" class="mb-2 flex-col text-[13px]" (click)="orderViaInvoice()">
          {{ 'MODAL_COMBIORDER_BUTTON_INVOICE' | translate }}
          <div class="text-[10px] font-sans font-light">+ € 3 {{ 'MODAL_COMBIORDER_BUTTON_INVOICE_COST' | translate }}</div>
        </Button>
        <span translate="MODAL_COMBIORDER_OR" class="mx-1"></span>
        <Button type="submit" color="blue" size="xsmall" [shadow]="false" (click)="orderViaMollie()">{{ 'MODAL_COMBIORDER_BUTTON_CREDITCARD' | translate }}</Button>
      </div>

      <div class="mt-3 text-xxs" [innerHTML]="'MODAL_COMBIORDER_INVOICE_EXPLANATION' | translate"></div>
      @if (user()?.mightHaveActiveMandate) {
        <div class="mt-3 text-xxs" [innerHTML]="'MODAL_COMBIORDER_DIRECTDEBIT_WITH_MANDATE_EXPLANATION' | translate"></div>
      } @else {
        <div class="mt-3 text-xxs" [innerHTML]="'MODAL_COMBIORDER_DIRECTDEBIT_EXPLANATION' | translate"></div>
      }
    </form>
  `
})
export class OrderFormComponent {
  readonly user = input<User>();
  @Input() settings: UserCombiOrderSettings;
  readonly combiOrder = input<CombiOrder>();
  readonly userCombiPackage = input<UserCombiPackage>();
  @Output() orderSuccess = new EventEmitter<CreateUserCombiOrderResult>();

  private processing: boolean;

  constructor(private translate: TranslateService,
              private combiOrderService: CombiOrderService,
              private userService: UserService) {
  }

  getPaymentFrequencies(): Array<SelectionListItem> {
    return [
      {value: "YEAR", displayName: this.translate.instant("MODAL_COMBIORDER_PERIOD_YEAR")},
      {value: "MONTH", displayName: this.translate.instant("MODAL_COMBIORDER_PERIOD_MONTH")}
    ];
  }

  async orderViaInvoice(): Promise<void> {
    const combiOrder = this.combiOrder();
    const userCombiPackage = this.userCombiPackage();
    logAddPaymentInfo(combiOrder.monthlyAmount, "invoice", userCombiPackage);
    const user = this.user();
    if (!user.isInMoneybird && (!this.settings.name || !this.settings.companyName)) {
      return;
    }
    if (this.processing) return;
    this.processing = true;

    const result = await this.combiOrderService.createWithInvoice(user, this.settings);
    if (result) {
      logPurchase(combiOrder.monthlyAmount, userCombiPackage, result.userCombiPackages.length == 1);
      this.userService.setUser(result.user);
      this.orderSuccess.emit(result);
    }
    this.processing = false;
  }

  async orderViaMollie(): Promise<void> {
    const combiOrder = this.combiOrder();
    const userCombiPackage = this.userCombiPackage();
    logAddPaymentInfo(combiOrder.monthlyAmount, "mollie", userCombiPackage);
    const user = this.user();
    if (!user.isInMoneybird && (!this.settings.name || !this.settings.companyName)) {
      return;
    }
    if (this.processing) return;
    this.processing = true;

    const result = await this.combiOrderService.createWithCreditCardViaMollie(user, this.settings);
    if (result) {
      logPurchase(combiOrder.monthlyAmount, userCombiPackage, result.userCombiPackages.length == 1);
      this.userService.setUser(result.user);
      window.top.location.href = result.paymentLink ?? ('/' + (this.settings.mollieRedirectUrl || 'dashboard'));
    }
    this.processing = false;
  }
}
